import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { getPort } from "@itaas/ports";

const EDU_API_BASE_URL =
  import.meta.env.VITE_EDU_API_BASE_URL ?? "/api/edu";
const EDU_API_PORT = getPort("edu", "api");

export class EduPlatformApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "EduPlatformApiError";
  }
}

export interface PlatformSchoolSummary {
  tenantSlug: string;
  organizationName: string | null;
  setupCompleted: boolean;
  setupCurrentStep: number;
  lifecycleStatus: string;
  archivedAt: string | null;
  purgedAt: string | null;
  studentCount: number;
  guardianCount: number;
  classroomCount: number;
  platformPlan: string | null;
  platformPlanDisplayName: string | null;
  platformBillingStatus: string;
  maxStudents: number | null;
  studentQuotaRemaining: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformSchoolDetail extends PlatformSchoolSummary {
  countryCode: string | null;
  pendingGuardianSync: number;
  pendingStudentSync: number;
  billing?: {
    tenantSlug: string;
    platformPlan: string | null;
    platformPlanDisplayName: string | null;
    platformBillingStatus: string;
    edition: string;
    maxStudents: number | null;
    enrolledStudentCount: number;
    studentQuotaRemaining: number | null;
    billingCycle: string;
  };
}

async function eduPlatformRequest<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const session = loadStoredSession();
  const token = session?.tokens?.accessToken;
  if (!token) {
    throw new EduPlatformApiError("Session expirée — reconnectez-vous.", 401);
  }

  const tenantSlug = session.tenant?.slug?.trim() || "demo";
  const method = options.method ?? "GET";

  const response = await fetch(`${EDU_API_BASE_URL}${path}`, {
    method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-Tenant-Slug": tenantSlug,
    },
    body:
      method === "GET" || method === "DELETE"
        ? undefined
        : JSON.stringify(options.body ?? {}),
  }).catch(() => {
    throw new EduPlatformApiError(
      `API Edu inaccessible. Démarrez l'API Edu (port ${EDU_API_PORT}).`,
      0,
    );
  });

  const text = await response.text();
  if (!response.ok) {
    let message = `Erreur API Edu (${response.status})`;
    try {
      const json = JSON.parse(text) as { message?: string };
      if (json.message) message = json.message;
    } catch {
      if (text.trim()) message = text;
    }
    throw new EduPlatformApiError(message, response.status);
  }

  if (!text.trim()) return {} as T;
  return JSON.parse(text) as T;
}

export function fetchPlatformSchools(): Promise<{ items: PlatformSchoolSummary[]; total: number }> {
  return eduPlatformRequest("/platform/tenants");
}

export function fetchPlatformSchool(slug: string): Promise<PlatformSchoolDetail> {
  return eduPlatformRequest(`/platform/tenants/${encodeURIComponent(slug)}`);
}

export function syncPlatformSchoolIam(
  slug: string,
  force = false,
): Promise<{
  guardians: { processed: number; succeeded: number; failed: number };
  students: { processed: number; succeeded: number; failed: number };
  iamEnabled: boolean;
}> {
  return eduPlatformRequest(`/platform/tenants/${encodeURIComponent(slug)}/sync-iam`, {
    method: "POST",
    body: { force },
  });
}

export function resetPlatformSchoolSetup(slug: string): Promise<PlatformSchoolSummary> {
  return eduPlatformRequest(`/platform/tenants/${encodeURIComponent(slug)}/reset-setup`, {
    method: "POST",
  });
}
